import {TeamWithMembers, User} from '@blesk-frontend/api';
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  Team as TeamIcon,
} from '@blesk-frontend/ui';
import {ChevronDown} from 'lucide-react';
import {useMemo} from 'react';

interface AppSidebarTeamProps {
  user?: User;
  teams?: TeamWithMembers[];
  onClickTeamSwitch: (teamId: string) => Promise<void>;
}

export const AppSidebarTeamMenu = ({user, teams, onClickTeamSwitch}: AppSidebarTeamProps) => {
  const currentTeam = useMemo(() => {
    return teams?.find((team) => team.id === (user?.teams.at(0)?.team.id as string));
  }, [teams, user]);

  const sortedTeams = useMemo(
    () =>
      teams?.sort((a, b) => {
        const aIsOwner = a.members.some(
          (member) => member.userId === user?.id && member.role === 'owner',
        );
        const bIsOwner = b.members.some(
          (member) => member.userId === user?.id && member.role === 'owner',
        );

        return aIsOwner === bIsOwner ? 0 : aIsOwner ? -1 : 1;
      }),
    [teams, user?.id],
  );

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild className="group">
        <div className="flex w-full cursor-pointer items-center gap-2 rounded-md border bg-bg-primary px-2 py-1.5 text-text-secondary">
          <TeamIcon className="size-6 shrink-0" />
          {currentTeam ? (
            <div className="truncate">{currentTeam.name}</div>
          ) : (
            <Skeleton className="h-[22px] w-full" />
          )}
          <ChevronDown className="ml-auto size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent sideOffset={8} className="w-52 space-y-1 text-text-secondary">
        {sortedTeams?.map((team) => {
          const owner = team.members.find((member) => member.role === 'owner');
          return (
            <DropdownMenuItem
              key={`team-dropdown-${team.id}`}
              className={cn('flex flex-col items-start', {
                'pointer-events-none bg-bg-primary': currentTeam?.id === team.id,
              })}
              onClick={() => onClickTeamSwitch(team.id)}
            >
              <p className="line-clamp-1 text-paragraph-sm text-text-secondary">{team.name}</p>
              <p className="text-paragraph-xs text-text-disabled">
                {owner?.userId === user?.id ? 'Моя команда' : owner?.user.email}
              </p>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
